import React, { useEffect, useState } from 'react';
import PageHeader from '../components/layout/PageHeader';
import { CalendarIcon, FilterIcon, SearchIcon, TagIcon, RefreshCwIcon, MoonIcon, SunIcon, GlobeIcon, ExternalLinkIcon } from 'lucide-react';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { newsArticles, NewsArticle } from '../data/newsArticles';
const News = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [filteredArticles, setFilteredArticles] = useState<NewsArticle[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [darkMode, setDarkMode] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const articlesPerPage = 9;
  // Simulate automatic refresh every 5 minutes
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      setLastUpdated(new Date());
    }, 300000); // 5 minutes
    return () => clearInterval(refreshInterval);
  }, []);
  // Filter articles based on search query, category, and region
  useEffect(() => {
    let results = [...newsArticles];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter(article => article.title.toLowerCase().includes(query) || article.excerpt.toLowerCase().includes(query) || article.tags.some(tag => tag.toLowerCase().includes(query)));
    }
    if (selectedCategory) {
      results = results.filter(article => article.category === selectedCategory);
    }
    if (selectedRegion) {
      results = results.filter(article => article.region === selectedRegion);
    }
    results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setFilteredArticles(results);
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedRegion]);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const getCategoryBadge = (category: string) => {
    const badges: Record<string, {
      variant: any;
      label: string;
    }> = {
      academics: {
        variant: 'primary',
        label: 'Academics'
      },
      sports: {
        variant: 'success',
        label: 'Sports'
      },
      events: {
        variant: 'warning',
        label: 'Events'
      },
      achievements: {
        variant: 'info',
        label: 'Achievements'
      },
      announcements: {
        variant: 'secondary',
        label: 'Announcements'
      },
      business: {
        variant: 'primary',
        label: 'Business'
      },
      technology: {
        variant: 'info',
        label: 'Technology'
      },
      health: {
        variant: 'success',
        label: 'Health'
      },
      education: {
        variant: 'warning',
        label: 'Education'
      }
    };
    return badges[category] || {
      variant: 'secondary',
      label: 'News'
    };
  };
  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive':
        return 'text-green-600';
      case 'negative':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastUpdated(new Date());
      setIsRefreshing(false);
    }, 1000);
  };
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  return <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} transition-colors duration-300`} data-id="element-4203">
      <PageHeader title="News & Updates" description="Stay informed with the latest news from verified sources worldwide" backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/hgRGwnKLZHVDTRdgag3Y5F/WhatsApp_Image_2025-10-24_at_10.01.11.jpg" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'News',
      path: '/news'
    }]} data-id="element-4204" />
      <div className="container mx-auto px-4 py-8" data-id="element-4205">
        {/* Controls Bar */}
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-4 mb-8 shadow-md`} data-id="element-4206">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-4207">
            <div className="flex items-center gap-4" data-id="element-4208">
              <button onClick={handleRefresh} disabled={isRefreshing} className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-900 hover:bg-blue-800'} text-white disabled:opacity-50`} data-id="element-4209">
                <RefreshCwIcon className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} data-id="element-4210" />
                Refresh
              </button>
              <div className="text-sm text-gray-500" data-id="element-4211">
                Last updated: {formatDate(lastUpdated.toISOString())}
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-4212">
              <div className="relative" data-id="element-4213">
                <input type="text" placeholder="Search news..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`pl-10 pr-4 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`} data-id="element-4214" />
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4215" />
              </div>
              <button onClick={() => setFilterOpen(!filterOpen)} className={`flex items-center px-4 py-2 rounded-md border ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'} transition-colors`} data-id="element-4216">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-4217" />
                Filters
              </button>
              <button onClick={() => setDarkMode(!darkMode)} className={`p-2 rounded-full ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`} data-id="element-4218">
                {darkMode ? <SunIcon className="h-5 w-5" data-id="element-4219" /> : <MoonIcon className="h-5 w-5" data-id="element-4220" />}
              </button>
            </div>
          </div>
          {/* Filter Panel */}
          {filterOpen && <div className="mt-4 pt-4 border-t border-gray-300 animate-fadeIn" data-id="element-4221">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4222">
                <div data-id="element-4223">
                  <h4 className="font-medium mb-2 flex items-center gap-2" data-id="element-4224">
                    <TagIcon className="h-4 w-4" data-id="element-4225" />
                    Category
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-4226">
                    <button onClick={() => setSelectedCategory(null)} className={`px-3 py-1 rounded-full text-sm transition-colors ${!selectedCategory ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-900 text-white' : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`} data-id="element-4227">
                      All
                    </button>
                    {['academics', 'sports', 'events', 'achievements', 'announcements', 'business', 'technology', 'health', 'education'].map(cat => <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-3 py-1 rounded-full text-sm transition-colors ${selectedCategory === cat ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-900 text-white' : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`} data-id="element-4228">
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>)}
                  </div>
                </div>
                <div data-id="element-4229">
                  <h4 className="font-medium mb-2 flex items-center gap-2" data-id="element-4230">
                    <GlobeIcon className="h-4 w-4" data-id="element-4231" />
                    Region
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-4232">
                    <button onClick={() => setSelectedRegion(null)} className={`px-3 py-1 rounded-full text-sm transition-colors ${!selectedRegion ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-900 text-white' : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`} data-id="element-4233">
                      All Regions
                    </button>
                    {['Nigeria', 'Africa', 'Global'].map(region => <button key={region} onClick={() => setSelectedRegion(region)} className={`px-3 py-1 rounded-full text-sm transition-colors ${selectedRegion === region ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-900 text-white' : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`} data-id="element-4234">
                        {region}
                      </button>)}
                  </div>
                </div>
              </div>
            </div>}
        </div>
        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10" data-id="element-4235">
          {currentArticles.map((article, index) => <div key={article.id} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 animate-fadeIn`} style={{
          animationDelay: `${index * 0.1}s`
        }} data-id="element-4236">
              <div className="relative h-48 overflow-hidden" data-id="element-4237">
                <img src={article.image} alt={article.title} className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500" data-id="element-4238" />
                {article.sentiment && <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-gray-900/80' : 'bg-white/80'} ${getSentimentColor(article.sentiment)}`} data-id="element-4239">
                    {article.sentiment === 'positive' ? '😊' : article.sentiment === 'negative' ? '😟' : '😐'}{' '}
                    {article.sentiment}
                  </div>}
              </div>
              <div className="p-5" data-id="element-4240">
                <div className="flex items-center justify-between mb-3" data-id="element-4241">
                  <Badge variant={getCategoryBadge(article.category).variant} data-id="element-4242">
                    {getCategoryBadge(article.category).label}
                  </Badge>
                  <div className="flex items-center text-sm text-gray-500" data-id="element-4243">
                    <CalendarIcon className="h-3 w-3 mr-1" data-id="element-4244" />
                    {formatDate(article.date)}
                  </div>
                </div>
                <h3 className={`text-lg font-bold mb-2 line-clamp-2 ${darkMode ? 'text-white' : 'text-blue-900'}`} data-id="element-4245">
                  {article.title}
                </h3>
                <p className={`text-sm mb-4 line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-4246">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-200" data-id="element-4247">
                  <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-4248">
                    {article.source && <>
                        <span className="font-medium" data-id="element-4249">{article.source}</span>
                        {article.region && <span data-id="element-4250">• {article.region}</span>}
                      </>}
                  </div>
                  <a href={article.sourceUrl || `/news/${article.slug}`} target={article.sourceUrl ? '_blank' : '_self'} rel={article.sourceUrl ? 'noopener noreferrer' : ''} className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-4251">
                    Read More <ExternalLinkIcon className="h-3 w-3" data-id="element-4252" />
                  </a>
                </div>
              </div>
            </div>)}
        </div>
        {/* No Results */}
        {filteredArticles.length === 0 && <div className={`text-center py-12 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg`} data-id="element-4253">
            <SearchIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-4254" />
            <h3 className="text-lg font-medium mb-2" data-id="element-4255">No articles found</h3>
            <p className="text-gray-600 mb-4" data-id="element-4256">
              Try adjusting your search or filters
            </p>
            <Button variant="outline" onClick={() => {
          setSearchQuery('');
          setSelectedCategory(null);
          setSelectedRegion(null);
        }} data-id="element-4257">
              Clear Filters
            </Button>
          </div>}
        {/* Pagination */}
        {filteredArticles.length > articlesPerPage && <div className="flex justify-center mt-8" data-id="element-4258">
            <nav className="inline-flex rounded-md shadow" data-id="element-4259">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className={`px-4 py-2 rounded-l-md border text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'} ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`} data-id="element-4260">
                Previous
              </button>
              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-4 py-2 border text-sm font-medium ${currentPage === page ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-900 text-white' : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'} ${darkMode ? 'border-gray-700' : 'border-gray-300'}`} data-id="element-4261">
                  {page}
                </button>)}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className={`px-4 py-2 rounded-r-md border text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'} ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'}`} data-id="element-4262">
                Next
              </button>
            </nav>
          </div>}
        {/* Newsletter Section */}
        <section className={`mt-16 ${darkMode ? 'bg-gray-800' : 'bg-blue-900'} text-white p-8 rounded-lg text-center`} data-id="element-4263">
          <h2 className="text-2xl font-bold mb-4" data-id="element-4264">
            Subscribe to Our Newsletter
          </h2>
          <p className="mb-6 max-w-2xl mx-auto" data-id="element-4265">
            Get the latest news delivered to your inbox. Stay informed with
            breaking news and updates.
          </p>
          <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-4" data-id="element-4266">
            <input type="email" placeholder="Your email address" className={`px-4 py-2 rounded-md flex-grow ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} focus:outline-none focus:ring-2 focus:ring-yellow-500`} data-id="element-4267" />
            <Button variant="primary" data-id="element-4268">Subscribe</Button>
          </div>
        </section>
      </div>
    </div>;
};
export default News;